/**
 * Cliente do laudo da modalidade assinatura eletrônica (E74.F3, ADR-0075 §7).
 *
 * As rotas são as MESMAS do laudo grafotécnico — `/laudo.pdf`, `/laudo.docx` e
 * `/laudo/preview.pdf` bifurcam por `case.modalidade` no servidor. O que muda é
 * a **recusa**: o gate eletrônico devolve um corpo estruturado
 * (`{code, motivo, pendentes}`) em vez do código seco do grafotécnico, porque
 * quem pede este documento é o perito do próprio caso e "falta concluir a
 * CCB-2" é o que o manda para a tela certa.
 *
 * Módulo separado de `lib/laudo` de propósito: aquele carrega narrativa,
 * critérios e aprovação, nada do que existe aqui. O que é comum — baixar blob,
 * nome do arquivo, disparar o download — é importado de lá, não copiado.
 */
import axios, { type AxiosInstance } from 'axios';
import { codigoDeVerificacao, type DocumentoBaixado } from '@/lib/http_download';

import { rehydrateBlobError } from '@/lib/laudo';

export type LaudoEletronicoMotivo =
  | 'sem_ccb'
  | 'exame_pendente'
  | 'revisao_pendente'
  /** E93 (F-E93.F2-03): documentos verificados com protocolos ou réguas diferentes. */
  | 'verificacao_desalinhada'
  /** E140.F2 (GUS-143): a leitura dos documentos tem falha técnica em conferência. */
  | 'leitura_em_conferencia'
  /** E180.F1 (GUS-254): a CCB verificada com a régua anterior, que a conclusão não segura mais. */
  | 'regua_anterior';

export type LaudoEletronicoErrorReason =
  | 'case_not_found'
  | 'not_ready'
  | 'modalidade_invalida'
  | 'render_failed'
  | 'network'
  | 'unknown';

export class LaudoEletronicoError extends Error {
  public readonly reason: LaudoEletronicoErrorReason;
  /** Preenchido só em `not_ready`: por que o documento não pode sair. */
  public readonly motivo: LaudoEletronicoMotivo | null;
  /**
   * Preenchido em `exame_pendente` e `verificacao_desalinhada` (as CCBs a resolver).
   */
  public readonly pendentes: readonly string[];

  constructor(
    reason: LaudoEletronicoErrorReason,
    motivo: LaudoEletronicoMotivo | null = null,
    pendentes: readonly string[] = [],
  ) {
    super(`laudo_eletronico_error:${reason}`);
    this.name = 'LaudoEletronicoError';
    this.reason = reason;
    this.motivo = motivo;
    this.pendentes = pendentes;
  }
}

const HTTP_NOT_FOUND = 404;
const HTTP_UNPROCESSABLE = 422;
const HTTP_INTERNAL = 500;

interface DetalheEstruturado {
  code?: unknown;
  motivo?: unknown;
  pendentes?: unknown;
}

function comoLista(valor: unknown): readonly string[] {
  return Array.isArray(valor) ? valor.filter((v): v is string => typeof v === 'string') : [];
}

/** Código seco → razão. Tabela para a classificação não virar cadeia de `if`. */
const _POR_CODIGO: Readonly<Record<string, LaudoEletronicoErrorReason>> = {
  CASE_NOT_FOUND: 'case_not_found',
  CASE_MODALIDADE_INVALIDA: 'modalidade_invalida',
  LAUDO_RENDER_FAILED: 'render_failed',
};

/** Fallback por status, quando o corpo não trouxe código conhecido. */
const _POR_STATUS: Readonly<Record<number, LaudoEletronicoErrorReason>> = {
  [HTTP_NOT_FOUND]: 'case_not_found',
  [HTTP_UNPROCESSABLE]: 'not_ready',
  [HTTP_INTERNAL]: 'render_failed',
};

/** A recusa do gate eletrônico, que vem com o motivo e as CCBs pendentes. */
function comoGate(detail: unknown): LaudoEletronicoError | null {
  if (typeof detail !== 'object' || detail === null) return null;
  const estruturado = detail as DetalheEstruturado;
  if (estruturado.code !== 'LAUDO_ELETRONICO_NOT_READY') return null;
  return new LaudoEletronicoError(
    'not_ready',
    (estruturado.motivo as LaudoEletronicoMotivo | undefined) ?? null,
    comoLista(estruturado.pendentes),
  );
}

export function classifyLaudoEletronicoError(err: unknown): LaudoEletronicoError {
  if (!axios.isAxiosError(err)) return new LaudoEletronicoError('unknown');
  if (err.response === undefined) return new LaudoEletronicoError('network');

  const detail = (err.response.data as { detail?: unknown } | undefined)?.detail;
  const gate = comoGate(detail);
  if (gate !== null) return gate;

  const porCodigo = typeof detail === 'string' ? _POR_CODIGO[detail] : undefined;
  return new LaudoEletronicoError(porCodigo ?? _POR_STATUS[err.response.status] ?? 'unknown');
}

/**
 * Baixa um dos três formatos do documento.
 *
 * O corpo de erro chega como `Blob` quando `responseType: 'blob'` — sem
 * reidratá-lo, toda recusa viraria "erro inesperado" e a tela perderia
 * justamente a informação acionável ("falta concluir a CCB-2").
 */
async function baixar(client: AxiosInstance, caminho: string): Promise<DocumentoBaixado> {
  try {
    const resposta = await client.get<Blob>(caminho, { responseType: 'blob' });
    // E85.F2: o código de verificação do arquivo entregue vem no cabeçalho.
    return { blob: resposta.data, codigoVerificacao: codigoDeVerificacao(resposta.headers) };
  } catch (err) {
    throw classifyLaudoEletronicoError(await rehydrateBlobError(err));
  }
}

export function getLaudoEletronicoPdf(
  client: AxiosInstance,
  caseId: string,
): Promise<DocumentoBaixado> {
  return baixar(client, `/casos/${caseId}/laudo.pdf`);
}

export function getLaudoEletronicoDocx(
  client: AxiosInstance,
  caseId: string,
): Promise<DocumentoBaixado> {
  return baixar(client, `/casos/${caseId}/laudo.docx`);
}

export async function getLaudoEletronicoPreview(
  client: AxiosInstance,
  caseId: string,
): Promise<Blob> {
  return (await baixar(client, `/casos/${caseId}/laudo/preview.pdf`)).blob;
}
